<template>
  <div class="modal-wrapper" :class="{ 'show': isOpen }">
    <div class="modal-overlay" @click="handleClose"></div>
    <div class="modal-sidebar shadow-lg">
      <div class="modal-header d-flex justify-content-between align-items-center p-3 border-bottom bg-light">
        <h5 class="m-0 fw-bold text-primary">Order Detail</h5>
        <button class="btn-close" @click="handleClose"></button>
      </div>

      <div class="modal-body p-4" v-if="order">
        <!-- Status Header -->
        <div class="card mb-4 border-0 bg-light-primary shadow-sm">
          <div class="card-body py-3 px-4">
            <div class="row align-items-center text-center text-md-start">
              <div class="col-md-4 mb-2 mb-md-0">
                <div class="text-muted mb-1" style="font-size: 13px;">Mã đơn hàng</div>
                <div class="fw-bold text-primary h5 m-0">{{ order.code }}</div>
              </div>
              <div class="col-md-4 mb-2 mb-md-0 border-start-md">
                <div class="text-muted mb-1" style="font-size: 13px;">Tracking Number</div>
                <div class="fw-bold h5 m-0">
                  <template v-if="isTrackingHiddenForCustomer">
                    <span class="text-danger" style="font-size: 14px;"><i class="fa fa-lock me-1"></i> Thanh toán để xem</span>
                  </template>
                  <template v-else>
                    {{ order.tracking_number || 'Chưa có' }}
                  </template>
                </div>
                <div v-if="order.buy_tracking_result && order.buy_tracking_result !== 'success'" class="mt-1">
                  <span class="status-badge" style="font-size: 11px;">
                    {{ order.buy_tracking_result }}
                  </span>
                </div>
                <div v-if="order.label_url && !isTrackingHiddenForCustomer" class="mt-1">
                  <a :href="order.label_url" target="_blank" class="text-primary small">
                    <i class="fa fa-file-text me-1"></i>Xem Label
                  </a>
                </div>
              </div>
              <div class="col-md-4 text-md-end">
                <div class="text-muted mb-1" style="font-size: 13px;">Trạng thái</div>
                <div class="d-flex align-items-center justify-content-md-end justify-content-center gap-2 flex-wrap">
                  <div class="status-badge py-2 px-3 fw-bold shadow-sm" :class="getStatusClass(order.status_id)" style="font-size: 14px;">
                    {{ getStatusName(order.status_id) }}
                  </div>
                  <button v-if="!isSale" class="btn btn-sm btn-outline-primary" @click="handleCopyOrder" title="Sao chép đơn này sang tạo đơn mới">
                    <i class="fa fa-copy me-1"></i> Copy Order
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <!-- Left Column -->
          <div class="col-md-6">
            <!-- Creator Info -->
            <div class="section mb-4 p-3 border rounded bg-white shadow-sm">
              <h6 class="border-bottom pb-2 text-dark fw-bold mb-3">
                <i class="fa fa-user-circle me-2 text-primary"></i>Người tạo
              </h6>
              <div class="row g-3">
                <div class="col-12"><strong>Tên:</strong> {{ order.customer?.name || 'N/A' }}</div>
                <div class="col-12"><strong>Điện thoại:</strong> {{ order.customer?.phone || 'N/A' }}</div>
                <div class="col-12"><strong>Email:</strong> {{ order.customer?.email || 'N/A' }}</div>
              </div>
            </div>

            <!-- Package Info -->
            <div class="section mb-4 p-3 border rounded bg-white shadow-sm">
              <h6 class="border-bottom pb-2 text-dark fw-bold mb-3">
                <i class="fa fa-cube me-2 text-primary"></i>Thông tin kiện hàng
              </h6>
              <div class="row g-3">
                <div class="col-6"><strong>Tên kiện hàng:</strong> {{ order.package_name }}</div>
                <div class="col-6"><strong>Kho nhận:</strong> {{ order.warehouse_id || 'N/A' }}</div>
                <div class="col-6"><strong>Dài (cm):</strong> {{ order.length }}</div>
                <div class="col-6"><strong>Rộng (cm):</strong> {{ order.width }}</div>
                <div class="col-6"><strong>Cao (cm):</strong> {{ order.height }}</div>
                <div class="col-6"><strong>Cân nặng (g):</strong> {{ order.weight }}</div>
                <div class="col-12"><strong>Dim weight (g):</strong> <span class="text-primary fw-bold">{{ calculateDimWeight(order) }}</span></div>
                <div class="col-12"><strong>Ghi chú từ khách:</strong> {{ order.customer_note || '--' }}</div>
              </div>
            </div>
          </div>

          <!-- Right Column -->
          <div class="col-md-6">
            <!-- Shipping Info -->
            <div class="section mb-4 p-3 border rounded bg-white shadow-sm h-100">
              <h6 class="border-bottom pb-2 text-dark fw-bold mb-3">
                <i class="fa fa-map-marker me-2 text-primary"></i>Thông tin gửi & nhận hàng
              </h6>
              <div class="row g-3">
                <div class="col-12">
                  <label class="d-block text-muted mb-0" style="font-size: 13px;">Tên đơn hàng</label>
                  <span class="fw-bold text-dark">{{ order.name }}</span>
                </div>
                <div class="col-6">
                  <label class="d-block text-muted mb-0" style="font-size: 13px;">Dịch vụ</label>
                  <span>{{ getServiceName(order.service_id) }}</span>
                </div>
                <div class="col-6">
                  <label class="d-block text-muted mb-0" style="font-size: 13px;">Giá trị đơn (USD)</label>
                  <span class="text-success fw-bold">${{ Number(order.price || 0).toLocaleString() }}</span>
                </div>
                <div class="col-12">
                  <label class="d-block text-muted mb-0" style="font-size: 13px;">Địa chỉ hồi trả</label>
                  <p class="mb-0">{{ order.return_address || '--' }}</p>
                </div>
              </div>

              <div class="receiver-box mt-3 pt-3 border-top">
                <div class="fw-bold text-dark mb-2">Người nhận</div>
                <div class="row g-2">
                  <div class="col-12"><strong>Họ tên:</strong> {{ order.receiver_name }}</div>
                  <div class="col-6"><strong>Điện thoại:</strong> {{ order.receiver_phone }}</div>
                  <div class="col-6"><strong>Email:</strong> {{ order.receiver_email }}</div>
                  <div class="col-12"><strong>Địa chỉ:</strong> {{ order.receiver_address }}</div>
                  <div class="col-6"><strong>Thành phố:</strong> {{ order.receiver_city }}</div>
                  <div class="col-6"><strong>Bang:</strong> {{ order.receiver_state }}</div>
                  <div class="col-6"><strong>Quốc gia:</strong> {{ getCountryName(order.receiver_country_id) }}</div>
                  <div class="col-6"><strong>Mã bưu điện:</strong> {{ order.receiver_post_code || '--' }}</div>
                  <div class="col-12" v-if="order.ioss_number"><strong>IOSS Number:</strong> {{ order.ioss_number }}</div>
                  <div class="col-12"><strong>Ghi chú người nhận:</strong> {{ order.receiver_note || '--' }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Items List -->
        <div class="section mb-4 p-3 border rounded bg-white shadow-sm">
          <h6 class="border-bottom pb-2 text-dark fw-bold mb-3">
            <i class="fa fa-list me-2 text-primary"></i>Danh sách hàng
          </h6>
          <div class="table-responsive">
            <table class="table table-hover table-bordered mb-0" style="font-size: 14px;">
              <thead class="bg-light">
                <tr>
                  <th class="ps-3 py-2">Tên</th>
                  <th class="py-2">HSCode</th>
                  <th class="py-2">Mã YunExpress</th>
                  <th class="py-2">Giá</th>
                  <th class="text-center py-2">SL</th>
                  <th class="py-2">Mô tả</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in order.order_items" :key="idx">
                  <td class="ps-3 py-2">{{ item.name }}</td>
                  <td class="py-2">{{ item.hscode }}</td>
                  <td class="py-2 text-break" style="max-width: 240px;">
                    <div v-if="item.mp_id"><span class="text-muted fw-semibold">MPId:</span> {{ item.mp_id }}</div>
                    <div v-if="item.nsp_id"><span class="text-muted fw-semibold">NSPId:</span> {{ item.nsp_id }}</div>
                    <div v-if="item.sp_id"><span class="text-muted fw-semibold">SPId:</span> {{ item.sp_id }}</div>
                    <span v-if="!item.mp_id && !item.nsp_id && !item.sp_id">—</span>
                  </td>
                  <td class="text-success py-2">${{ Number(item.price || 0).toLocaleString() }}</td>
                  <td class="text-center py-2">{{ item.quantity }}</td>
                  <td class="py-2">{{ item.description }}</td>
                </tr>
                <tr v-if="!order.order_items?.length">
                  <td colspan="6" class="text-center text-muted py-3">Không có sản phẩm</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Fees Section (Operational roles) -->
        <div class="section mb-4 p-3 border rounded bg-white shadow-sm" v-if="canManageFees">
          <div class="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
            <h6 class="text-dark fw-bold m-0">
              <i class="fa fa-dollar me-2 text-primary"></i>Chi phí
            </h6>
            <button class="btn btn-xs btn-primary" @click="handleAddFeeRow">
              <i class="fa fa-plus me-1"></i>Thêm phí
            </button>
          </div>
          
          <div class="table-responsive">
            <table class="table table-sm table-hover table-bordered mb-0" style="font-size: 14px;">
              <thead class="bg-light">
                <tr>
                  <th class="ps-3 py-2">Tên phí</th>
                  <th class="py-2 text-end">Số tiền</th>
                  <th class="py-2 text-center" style="width: 150px; min-width: 150px;">Action</th>
                </tr>
              </thead>
              <tbody>
                <!-- Existing Fees -->
                <tr v-for="(fee, idx) in order.fees" :key="'fee-' + idx">
                  <td class="ps-3 py-2">{{ fee.fee_name }}</td>
                  <td class="py-2 text-end fw-bold">{{ Number(fee.fee_amount || 0).toLocaleString() }} đ</td>
                  <td class="py-2 text-center">
                    <span class="badge bg-light text-success border">Đã lưu</span>
                  </td>
                </tr>
                
                <!-- New Unsaved Fee Rows -->
                <tr v-for="(fee, idx) in newFees" :key="'new-fee-' + idx">
                  <td class="p-1">
                    <input type="text" v-model="fee.fee_name" class="form-control form-control-sm border-primary" placeholder="Tên phí...">
                  </td>
                  <td class="p-1">
                    <input type="number" v-model="fee.fee_amount" class="form-control form-control-sm border-primary text-end" placeholder="0">
                  </td>
                  <td class="p-1 text-center">
                    <button class="btn btn-sm btn-danger me-2" @click="handleRemoveFeeRow(idx)" v-if="!fee.submitting" title="Xóa hàng">
                      <i class="fa fa-trash"></i>
                    </button>
                    <button class="btn btn-sm btn-success px-3" @click="handleSubmitFee(fee, idx)" :disabled="fee.submitting" title="Lưu">
                      <i class="fa fa-check" v-if="!fee.submitting"></i>
                      <span class="spinner-border spinner-border-sm" v-else></span>
                    </button>
                  </td>
                </tr>

                <tr v-if="!order.fees?.length && !newFees.length">
                  <td colspan="3" class="text-center text-muted py-3">Chưa có phí nào</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Expenses Section (Admin only) -->
        <div class="section mb-4 p-3 border rounded bg-white shadow-sm" v-if="isAdmin">
          <div class="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
            <h6 class="text-dark fw-bold m-0">
              <i class="fa fa-money me-2 text-danger"></i>Expense
            </h6>
            <button class="btn btn-xs btn-danger" @click="handleAddExpenseRow">
              <i class="fa fa-plus me-1"></i>Thêm expense
            </button>
          </div>

          <div class="table-responsive">
            <table class="table table-sm table-hover table-bordered mb-0" style="font-size: 14px;">
              <thead class="bg-light">
                <tr>
                  <th class="ps-3 py-2">Tên chi phí</th>
                  <th class="py-2 text-end">Số tiền</th>
                  <th class="py-2 text-center" style="width: 150px; min-width: 150px;">Action</th>
                </tr>
              </thead>
              <tbody>
                <!-- Existing Expenses -->
                <tr v-for="(expense, idx) in order.expenses" :key="'expense-' + (expense.id ?? idx)">
                  <td class="ps-3 py-2">{{ expense.name }}</td>
                  <td class="py-2 text-end fw-bold">{{ Number(expense.amount || 0).toLocaleString() }} đ</td>
                  <td class="py-2 text-center">
                    <button class="btn btn-sm btn-danger" @click="handleDeleteExpense(expense, idx)" :disabled="expense.deleting" title="Xóa">
                      <i class="fa fa-trash" v-if="!expense.deleting"></i>
                      <span class="spinner-border spinner-border-sm" v-else></span>
                    </button>
                  </td>
                </tr>

                <!-- New Unsaved Expense Rows -->
                <tr v-for="(expense, idx) in newExpenses" :key="'new-expense-' + idx">
                  <td class="p-1">
                    <input type="text" v-model="expense.name" class="form-control form-control-sm border-danger" placeholder="Tên chi phí...">
                  </td>
                  <td class="p-1">
                    <input type="number" v-model="expense.amount" class="form-control form-control-sm border-danger text-end" placeholder="0">
                  </td>
                  <td class="p-1 text-center">
                    <button class="btn btn-sm btn-danger me-2" @click="handleRemoveExpenseRow(idx)" v-if="!expense.submitting" title="Xóa hàng">
                      <i class="fa fa-trash"></i>
                    </button>
                    <button class="btn btn-sm btn-success px-3" @click="handleSubmitExpense(expense, idx)" :disabled="expense.submitting" title="Lưu">
                      <i class="fa fa-check" v-if="!expense.submitting"></i>
                      <span class="spinner-border spinner-border-sm" v-else></span>
                    </button>
                  </td>
                </tr>

                <tr v-if="!order.expenses?.length && !newExpenses.length">
                  <td colspan="3" class="text-center text-muted py-3">Chưa có chi phí nào</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Fee Footer -->
        <div class="section mt-4 p-4 bg-primary text-white rounded-3 shadow">
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex flex-column">
              <span class="small opacity-75">TỔNG CỘNG</span>
              <small class="opacity-50">Đã bao gồm các loại thuế phí</small>
            </div>
            <span class="h4 m-0 fw-bold">{{ Number(order.total_order_fee || 0).toLocaleString() }} đ</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useOrders } from "@/composables/useOrders";
import { createToaster } from "@meforma/vue-toaster";
import { useCurrentUser } from "@/composables/useCurrentUser";
import { canViewTrackingBeforePayment } from "@/constants/trackingVisibility";

const router = useRouter();

const props = defineProps({
  isOpen: Boolean,
  order: Object,
  services: {
    type: Array,
    default: () => []
  },
  statuses: {
    type: Array,
    default: () => []
  },
  countries: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['close', 'refresh']);

const toaster = createToaster({ position: "top-right", duration: 3000 });
const { addCustomFeeApi, createExpenseApi, deleteExpenseApi } = useOrders();
const { currentUser, loadCurrentUser } = useCurrentUser();

// Các dịch vụ nhập tracking thủ công luôn hiển thị tracking và label cho khách hàng.
const MANUAL_TRACKING_SERVICE_IDS = [8, 9];

const newFees = reactive([]);
const newExpenses = reactive([]);

const isAdmin = computed(() => {
  const role = localStorage.getItem('user_role')?.toLowerCase();
  if (!role) return false;
  // Admin, Owner, System Owner or anything not strictly limited
  const restrictedRoles = ['customer', 'operation', 'accountant', 'guest'];
  return !restrictedRoles.includes(role) || role === 'admin' || role === 'owner';
});

const canManageFees = computed(() => {
  const role = localStorage.getItem('user_role')?.toLowerCase();
  return isAdmin.value || role === 'operation';
});


const isCustomer = computed(() => {
  return currentUser.value?.roles?.some(role => role.name.toLowerCase() === 'customer')
    ?? localStorage.getItem('user_role')?.toLowerCase() === 'customer';
});

const isTrackingHiddenForCustomer = computed(() => {
  return isCustomer.value
    && Number(props.order?.remain_fee) > 0
    && !MANUAL_TRACKING_SERVICE_IDS.includes(Number(props.order?.service_id))
    && !canViewTrackingBeforePayment(currentUser.value?.email);
});

onMounted(() => loadCurrentUser());

const isSale = computed(() => {
  return localStorage.getItem('user_role')?.toLowerCase() === 'sale';
});

// Sao chép đơn hiện tại sang màn tạo đơn mới.
// Chỉ lấy các field có input ở form Add, bỏ tracking_number/label_url/id/status... theo yêu cầu.
const handleCopyOrder = () => {
  const o = props.order;
  if (!o) return;

  const prefill = {
    service_id: o.service_id,
    name: o.name,
    price: o.price,
    note: o.note,
    customer_note: o.customer_note,
    return_address: o.return_address,
    is_insurance: !!o.is_insurance,
    receiver_name: o.receiver_name,
    receiver_phone: o.receiver_phone,
    receiver_email: o.receiver_email,
    receiver_address: o.receiver_address,
    receiver_city: o.receiver_city,
    receiver_state: o.receiver_state,
    receiver_country_id: o.receiver_country_id,
    receiver_note: o.receiver_note,
    receiver_post_code: o.receiver_post_code,
    ioss_number: o.ioss_number,
    package_name: o.package_name,
    length: o.length,
    width: o.width,
    height: o.height,
    weight: o.weight,
    warehouse_id: o.warehouse_id,
    order_items: (o.order_items || []).map(it => ({
      name: it.name,
      hscode: it.hscode,
      mp_id: it.mp_id,
      nsp_id: it.nsp_id,
      sp_id: it.sp_id,
      price: it.price,
      quantity: it.quantity,
      description: it.description
    }))
  };

  sessionStorage.setItem('order_copy_prefill', JSON.stringify(prefill));
  emit('close');
  router.push({ name: 'indexAdd' });
};

const handleAddFeeRow = () => {
  newFees.push({
    fee_name: '',
    fee_amount: 0,
    submitting: false
  });
};

const handleRemoveFeeRow = (idx) => {
  newFees.splice(idx, 1);
};

const handleSubmitFee = async (fee, idx) => {
  if (!fee.fee_name || fee.fee_amount === '' || fee.fee_amount === null || fee.fee_amount === undefined) {
    return toaster.error("Vui lòng nhập tên phí và số tiền hợp lệ");
  }

  fee.submitting = true;
  try {
    const payload = {
      order_id: props.order.id,
      fee_name: fee.fee_name,
      fee_amount: fee.fee_amount
    };

    const res = await addCustomFeeApi(payload);
    if (res.success) {
      toaster.success("Thêm phí thành công!");
      // Add to local list to reflect changes immediately
      if (!props.order.fees) props.order.fees = [];
      props.order.fees.push({
        fee_name: fee.fee_name,
        fee_amount: fee.fee_amount
      });
      
      // Update the total fee if possible (local approximation)
      props.order.total_order_fee = Number(props.order.total_order_fee) + Number(fee.fee_amount);
      
      // Remove from temporary list
      newFees.splice(idx, 1);
    } else {
      const errorMsg = res.error || (res.data && res.data.message) || "Không thể thêm phí";
      toaster.error(errorMsg);
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra khi gọi API");
  } finally {
    fee.submitting = false;
  }
};

// ─── Expenses (Admin only, internal cost) ────────────────────────────────────
const handleAddExpenseRow = () => {
  newExpenses.push({
    name: '',
    amount: 0,
    submitting: false
  });
};

const handleRemoveExpenseRow = (idx) => {
  newExpenses.splice(idx, 1);
};

const handleSubmitExpense = async (expense, idx) => {
  if (!expense.name || expense.amount === '' || expense.amount === null || expense.amount === undefined) {
    return toaster.error("Vui lòng nhập tên chi phí và số tiền hợp lệ");
  }

  expense.submitting = true;
  try {
    const payload = {
      order_id: props.order.id,
      name: expense.name,
      amount: expense.amount
    };

    const res = await createExpenseApi(payload);
    if (res.success) {
      toaster.success("Thêm chi phí thành công!");
      // Reflect changes immediately with the saved record (incl. id for delete)
      if (!props.order.expenses) props.order.expenses = [];
      props.order.expenses.unshift(res.data?.data ?? {
        name: expense.name,
        amount: expense.amount
      });

      newExpenses.splice(idx, 1);
    } else {
      const errorMsg = res.error || (res.data && res.data.message) || "Không thể thêm chi phí";
      toaster.error(errorMsg);
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra khi gọi API");
  } finally {
    expense.submitting = false;
  }
};

const handleDeleteExpense = async (expense, idx) => {
  if (!expense.id) {
    // No id (shouldn't happen for saved rows) — just drop locally
    props.order.expenses.splice(idx, 1);
    return;
  }

  expense.deleting = true;
  try {
    const res = await deleteExpenseApi(expense.id);
    if (res.success) {
      toaster.success("Xóa chi phí thành công!");
      props.order.expenses.splice(idx, 1);
    } else {
      const errorMsg = res.error || (res.data && res.data.message) || "Không thể xóa chi phí";
      toaster.error(errorMsg);
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra khi gọi API");
  } finally {
    expense.deleting = false;
  }
};

const handleClose = () => {
  newFees.length = 0; // Clear unsaved rows
  newExpenses.length = 0;
  emit('close');
};

const getServiceName = (id) => {
  const service = props.services.find(s => s.id === id);
  return service ? service.name : 'Unknown';
};

const getCountryName = (id) => {
  if (!id) return '--';
  const country = props.countries.find(c => c.id === id);
  return country ? country.display_name : `ID: ${id}`;
};

const getStatusName = (id) => {
  const status = props.statuses.find(s => s.id === id);
  return status ? status.display_name : 'Unknown';
};

const calculateDimWeight = (order) => {
  if (!order.length || !order.width || !order.height) return 0;
  return ((order.length * order.width * order.height) / 5).toFixed(1);
};

const getStatusClass = (id) => {
  const map = {
    10: 'status-new',          // Mới - Chờ xác nhận (Blue)
    11: 'status-conf',         // Đã xác nhận - Chờ hàng về (Indigo)
    12: 'status-wh',           // Hàng về kho - Chờ đóng gói (Orange)
    14: 'status-transmit',     // Đang vận chuyển (Primary)
    15: 'status-done',         // Hoàn thành (Success)
    16: 'status-return',       // Trả về (Purple)
    17: 'status-reserve',      // Chờ lưu kho (Teal)
    18: 'status-cancel',       // Hủy (Red)
    19: 'status-update',       // Phí thay đổi (Pink)
    20: 'status-loss',         // Thất lạc (Dark)
    21: 'status-reimburse',    // Bồi thường (Yellow)
  };
  return map[id] || 'status-default';
};
</script>

<style scoped>
.modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1050;
  visibility: hidden;
  transition: visibility 0.3s;
}

.modal-wrapper.show {
  visibility: visible;
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s;
}

.modal-wrapper.show .modal-overlay {
  opacity: 1;
}

.modal-sidebar {
  position: absolute;
  top: 0;
  right: -1100px;
  width: 1100px;
  max-width: 95%;
  height: 100%;
  background: #f8f9fa;
  transition: right 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
}

.modal-wrapper.show .modal-sidebar {
  right: 0;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  font-size: 14.5px;
}

.modal-body strong {
  font-weight: 600;
  color: #333;
}

.modal-body .row > div {
  word-break: break-word;
}

.border-start-md {
  border-left: 1px solid #dee2e6;
}

@media (max-width: 768px) {
  .border-start-md {
    border-left: none;
  }
}

.bg-light-primary {
  background-color: #f0f7ff;
}

.status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 50px;
  font-size: 11px;
  font-weight: 600;
  padding-left: 0;
}

.btn-xs {
  padding: 0.1rem 0.4rem;
  font-size: 0.75rem;
  line-height: 1.5;
  border-radius: 0.2rem;
}

/* Color Mapping for Statuses - Consistent with the list */
.status-new { background-color: #e0f2fe; color: #0369a1; }
.status-conf { background-color: #e0e7ff; color: #4338ca; }
.status-wh { background-color: #ffedd5; color: #c2410c; }
.status-transmit { background-color: #dbeafe; color: #1d4ed8; }
.status-done { background-color: #d1fae5; color: #047857; }
.status-return { background-color: #f3e8ff; color: #7e22ce; }
.status-reserve { background-color: #ccfbf1; color: #0f766e; }
.status-cancel { background-color: #fee2e2; color: #b91c1c; }
.status-update { background-color: #fce7f3; color: #be185d; }
.status-loss { background-color: #111827; color: #ffffff; }
.status-reimburse { background-color: #fef9c3; color: #a16207; }
.status-default { background-color: #f9fafb; color: #6b7280; }

@media (max-width: 1100px) {
  .modal-sidebar {
    width: 100%;
    right: -100%;
  }
}
</style>
